import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import socketManager from '../utils/SocketManager';

/**
 * Hook to manage socket connection lifecycle
 * Automatically connects when user is available and disconnects on unmount
 */
export const useSocketConnection = () => {
  const { user } = useSelector((state) => state.user);
  const hasConnected = useRef(false);

  useEffect(() => {
    if (user?.user?._id && user?.user?.username) {
      const userId = user.user._id;
      const username = user.user.username;

      // Connect only once per user session
      if (!hasConnected.current) {
        socketManager.connect(userId, username);
        hasConnected.current = true;
      }
    }

    // Cleanup on unmount (app-level only)
    return () => {
      // Don't disconnect on component unmount, only on app unmount
      // The socket should persist across route changes
    };
  }, [user?.user?._id, user?.user?.username]);

  return socketManager;
};

/**
 * Hook to subscribe to specific socket events
 * @param {string} event - Event name to listen to
 * @param {function} handler - Handler function
 * @param {array} dependencies - Dependencies array for the handler
 */
export const useSocketEvent = (event, handler, dependencies = []) => {
  const handlerRef = useRef(handler);

  // Update handler ref when dependencies change
  useEffect(() => {
    handlerRef.current = handler;
  }, dependencies);

  useEffect(() => {
    // Wrapper to always call the latest handler
    const wrappedHandler = (data) => {
      handlerRef.current(data);
    };

    // Subscribe to event
    const unsubscribe = socketManager.on(event, wrappedHandler);

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [event]);
};

/**
 * Hook to subscribe to user-action events with action type filtering
 * @param {string|array} actionTypes - Single action type or array of action types
 * @param {function} handler - Handler function
 * @param {array} dependencies - Dependencies array for the handler
 */
export const useSocketAction = (actionTypes, handler, dependencies = []) => {
  const types = Array.isArray(actionTypes) ? actionTypes : [actionTypes];

  useSocketEvent('user-action', (data) => {
    if (types.includes(data.action)) {
      handler(data);
    }
  }, dependencies);
};
